import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { strings } from '/imports/strings';
import { formatDateTime } from '/imports/utils';
import { ModelClass, Docmnt, ListMethodArg, ListMethodResponse } from '/imports/types';
import { Button } from '/imports/ui/Button';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { Icon } from '/imports/ui/Icon';
import { Page } from '/imports/ui/Page';
import { PageHeading } from '/imports/ui/PageHeading';
import { PageBody } from '/imports/ui/PageBody';
import { useUser } from '/imports/hooks';
import { createActiveFilter } from '/imports/ui/ActiveFilter';
import { ListMethodCatalog, ListMethodCatalogProps } from '/imports/ui/ListMethodCatalog';
import { Column } from '/imports/ui/DataTable';
import { MethodDeclaration } from '/imports/methods';

// Tailwind wrapper replacing the old `styled(Page)`. The descendant utilities
// reproduce the old rules one-for-one: `.page-body { height: 0 }` (so the inner
// table scrolls, not the page body) and `img { max-height: 3.5rem }`.
export const ModelListPageStyled = (props: React.ComponentProps<typeof Page>) => (
  <Page
    {...props}
    className={[props.className ?? '', '[&_.page-body]:h-0', '[&_img]:max-h-14'].join(' ')}
  />
);

interface ModelListPageProps<M extends Docmnt> extends Omit<ListMethodCatalogProps<M>, 'listMethod' | 'allIds'> {
  modelClass: ModelClass<M>;
  columns: Column<M>[];
  permission?: (user?: Meteor.User) => boolean | undefined;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  defaultSort?: { [k in keyof M]?: 1 | -1 };
  query?: Mongo.Query<M> | (() => Mongo.Query<M>);
  tools?: React.ReactNode;
  extraFilters?: React.ReactNode | (() => React.ReactNode);
  /** Opt out of auto-linking the first column to the edit page. */
  noRowLink?: boolean;
  styled?: any;
  className?: string;
  listMethod?: MethodDeclaration<ListMethodArg<M>, ListMethodResponse<M>>;
  title?: string;
  newButtonText?: string;
}

export const ModelListPage = <M extends Docmnt>(props: ModelListPageProps<M>) => {
  const { permission, modelClass, columns, header, footer, defaultSort, extraFilters, className, styled, tools, data, noRowLink } = props;
  const { pathPrefix, pluralName, icon, updateMethod, deleteMethod } = modelClass;
  const listMethod = props.listMethod || modelClass.listMethod;
  if (!listMethod || !updateMethod || !pathPrefix || !pluralName) {
    throw `modelClass is misconfigured for ModelFormPage: ${modelClass.collection}`;
  }
  const user = useUser();
  const propsQuery = typeof props.query === 'function' ? props.query() : props.query;
  const [query, setQuery] = useState<Mongo.Query<M>>({ active: true, ...propsQuery } as any);
  // Merge-with-removal, not a plain merge: when a page's query() stops
  // returning a key (CardsPage picking "All sets" drops setName), the plain
  // spread left the stale key in state forever, so the filter could never be
  // cleared. Keys the previous propsQuery owned but the new one dropped are
  // deleted before merging.
  const prevPropsQuery = useRef<Mongo.Query<M> | undefined>(propsQuery);
  useEffect(() => {
    setQuery((q) => {
      const next: any = { ...q };
      for (const k of Object.keys(prevPropsQuery.current || {})) {
        if (!(k in (propsQuery || {}))) delete next[k];
      }
      prevPropsQuery.current = propsQuery;
      return { ...next, ...propsQuery };
    });
  }, [JSON.stringify(propsQuery || {})]);
  /**
   * Every canonical list needs a way into its edit page. Rather than relying on
   * each screen remembering to add one (12 of 19 had not), link the FIRST
   * column automatically.
   *
   * Skipped when that column defines its own `render` -- that is the signal the
   * screen is already doing something deliberate there, which is how the pages
   * that render their own <Link> stay untouched. Mirrors DataTable's default
   * cell expression so Date formatting is preserved.
   */
  const linkedColumns = useMemo(() => {
    if (noRowLink || !columns?.length || !pathPrefix) return columns;
    const [first, ...rest] = columns as any[];
    if (!first || first.render) return columns;
    return [{
      ...first,
      render: (doc: any) => {
        const value = doc?.[first.field];
        const shown = value instanceof Date ? formatDateTime(value) : value;
        return (
          <Link className="text-primary hover:text-primary-2 hover:underline" to={`${pathPrefix}/${doc?._id}`}>
            {shown === undefined || shown === null || shown === '' ? String(doc?._id ?? '') : shown}
          </Link>
        );
      },
    }, ...rest] as typeof columns;
  }, [columns, pathPrefix, noRowLink]);

  const ActiveFilter = useMemo(() => createActiveFilter<M>(query, setQuery), []);
  if ((permission && !permission?.(user)) && user && !Meteor.loggingIn()) {
    return <NotPermittedPage />;
  }
  return React.createElement(styled || ModelListPageStyled, {
    className: `page model-list-page ${className || ''}`,
    breadcrumbs: [{ path: pathPrefix, label: pluralName }]
  },
  <>
    {
      header ||
        <PageHeading
          left={<h1>{icon && <Icon icon={icon} /> || null}{props.title || pluralName}</h1>}
          right={<div><Link to={`${pathPrefix}/new`}><Button className="btn primary new-button">{props.newButtonText || strings.New()}</Button></Link></div>}
        />
    }
    <PageBody>
      <ListMethodCatalog
        {...props}
        data={data || []}
        columns={linkedColumns as Column<Docmnt>[]}
        query={query}
        defaultSort={defaultSort}
        listMethod={listMethod}
        updateMethod={updateMethod as MethodDeclaration<Partial<Docmnt>, string>}
        deleteMethod={deleteMethod}
        tools={
          tools ||
            <div className="filters">
              <span className="filters-label">{strings.Filters()}:</span>
              {typeof extraFilters === 'function' ? extraFilters() : extraFilters}
              <ActiveFilter.Component />
            </div>
        }
      />
      { footer || null }
    </PageBody>
  </>
  );
};